import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErrorApi, api } from '../api/cliente';
import { useSesion } from '../auth/sesion';
import {
  Aviso,
  AreaTexto,
  Campo,
  Cargando,
  Casilla,
  Encabezado,
  Etiqueta,
  Seleccion,
  Tarjeta,
  Vacio,
} from '../ui/piezas';

/**
 * Las oficinas.
 *
 * El módulo existía entero en la API y no había pantalla, así que la dirección
 * y el teléfono de la sede principal estaban vacíos desde el primer día. Eso no
 * es un detalle interno: son los datos que el portal publica para que alguien
 * se fíe lo suficiente como para entregar su casa.
 */

interface Sede {
  id: string;
  codigo: string;
  nombre: string;
  slug: string;
  ciudadId: string;
  ciudad: string;
  departamento: string;
  direccion: string | null;
  telefono: string | null;
  whatsapp: string | null;
  email: string | null;
  horario: Record<string, string> | null;
  descripcion: string | null;
  esPrincipal: boolean;
  activa: boolean;
  orden: number;
}

interface Ciudad {
  id: string;
  nombre: string;
  departamento: string;
}

/**
 * Los días, en el orden en que se leen.
 *
 * El horario es un mapa libre en la base, y sin un orden fijo aquí acabaría
 * publicándose «sábado, lunes, miércoles» según cómo se escribiera.
 */
const DIAS = [
  { clave: 'lunes-viernes', texto: 'Lunes a viernes' },
  { clave: 'sabado', texto: 'Sábado' },
  { clave: 'domingo', texto: 'Domingo y festivos' },
];

interface Forma {
  codigo: string;
  nombre: string;
  slug: string;
  ciudadId: string;
  direccion: string;
  telefono: string;
  whatsapp: string;
  email: string;
  descripcion: string;
  horario: Record<string, string>;
  activa: boolean;
}

const VACIA: Forma = {
  codigo: '',
  nombre: '',
  slug: '',
  ciudadId: '',
  direccion: '',
  telefono: '',
  whatsapp: '',
  email: '',
  descripcion: '',
  horario: {},
  activa: true,
};

function texto(valor: string): string | null {
  const limpio = valor.trim();
  return limpio === '' ? null : limpio;
}

export default function Sedes() {
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [editando, setEditando] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [forma, setForma] = useState<Forma>(VACIA);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  const listado = useQuery({
    queryKey: ['sedes-admin'],
    queryFn: () => api<Sede[]>('/admin/sedes'),
  });

  const ciudades = useQuery({
    queryKey: ['ciudades'],
    queryFn: () => api<Ciudad[]>('/public/geografia/ciudades'),
  });

  useEffect(() => {
    if (!editando) return;
    const sede = (listado.data ?? []).find((s) => s.id === editando);
    if (!sede) return;
    setForma({
      codigo: sede.codigo,
      nombre: sede.nombre,
      slug: sede.slug,
      ciudadId: sede.ciudadId,
      direccion: sede.direccion ?? '',
      telefono: sede.telefono ?? '',
      whatsapp: sede.whatsapp ?? '',
      email: sede.email ?? '',
      descripcion: sede.descripcion ?? '',
      horario: sede.horario ?? {},
      activa: sede.activa,
    });
  }, [editando, listado.data]);

  const cuerpo = () => ({
    codigo: forma.codigo.trim().toUpperCase(),
    nombre: forma.nombre.trim(),
    slug: texto(forma.slug),
    ciudadId: forma.ciudadId,
    direccion: texto(forma.direccion),
    telefono: texto(forma.telefono),
    whatsapp: texto(forma.whatsapp),
    email: texto(forma.email),
    descripcion: texto(forma.descripcion),
    // Los días sin texto no viajan: un horario con huecos vacíos se publica
    // como «Sábado: » y queda peor que no decir nada.
    horario: Object.fromEntries(
      Object.entries(forma.horario).filter(([, valor]) => valor.trim() !== ''),
    ),
    activa: forma.activa,
  });

  const guardar = useMutation({
    mutationFn: () =>
      creando
        ? api<Sede>('/admin/sedes', { metodo: 'POST', cuerpo: cuerpo() })
        : api<Sede>(`/admin/sedes/${editando}`, { metodo: 'PUT', cuerpo: cuerpo() }),
    onSuccess: () => {
      setError(null);
      setExito('Guardado. El portal lo muestra en el próximo minuto.');
      setEditando(null);
      setCreando(false);
      void cliente.invalidateQueries({ queryKey: ['sedes-admin'] });
      void cliente.invalidateQueries({ queryKey: ['sedes'] });
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const marcarPrincipal = useMutation({
    mutationFn: (id: string) =>
      api<Sede>(`/admin/sedes/${id}/principal`, { metodo: 'POST' }),
    onSuccess: () => {
      setError(null);
      void cliente.invalidateQueries({ queryKey: ['sedes-admin'] });
    },
    onError: (e) => setError(e),
  });

  const campo = (nombre: string) =>
    error instanceof ErrorApi ? error.mensajeDe(nombre) : undefined;

  const editable = puede('sede:editar');
  const sedes = listado.data ?? [];
  const abierto = creando || editando !== null;

  return (
    <>
      <Encabezado
        titulo="Oficinas"
        nota="Lo que se publica en el portal: dirección, teléfono y horario."
      >
        {puede('sede:editar') && !abierto && (
          <button
            className="plano"
            onClick={() => {
              setForma(VACIA);
              setCreando(true);
              setEditando(null);
            }}
          >
            Nueva oficina
          </button>
        )}
      </Encabezado>

      <Aviso error={error} exito={exito} />

      {listado.isPending ? (
        <Cargando />
      ) : sedes.length === 0 ? (
        <Vacio>Todavía no hay oficinas registradas.</Vacio>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sedes.map((s) => (
            <Tarjeta key={s.id}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <b>{s.nombre}</b>
                <Etiqueta>{s.codigo}</Etiqueta>
                {s.esPrincipal && <small style={{ opacity: 0.7 }}>· principal</small>}
                {!s.activa && <small style={{ color: '#B77714' }}>· inactiva</small>}
              </div>

              <div style={{ marginTop: 8, fontSize: 14, opacity: 0.85 }}>
                <div>
                  {s.ciudad}, {s.departamento}
                </div>
                {/* Lo que falta se dice, no se calla: son los datos que el
                    portal necesita para que alguien se fíe. */}
                {s.direccion ? (
                  <div>{s.direccion}</div>
                ) : (
                  <div style={{ color: '#B77714' }}>Sin dirección — no se publica ninguna</div>
                )}
                {s.telefono ? (
                  <div>{s.telefono}</div>
                ) : (
                  <div style={{ color: '#B77714' }}>Sin teléfono</div>
                )}
                {s.email && <div>{s.email}</div>}
              </div>

              {editable && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="plano"
                    style={{ paddingLeft: 0 }}
                    onClick={() => {
                      setCreando(false);
                      setEditando(editando === s.id ? null : s.id);
                    }}
                  >
                    {editando === s.id ? 'Cerrar' : 'Editar'}
                  </button>
                  {!s.esPrincipal && puede('sede:editar') && (
                    <button
                      className="plano"
                      disabled={marcarPrincipal.isPending}
                      onClick={() => marcarPrincipal.mutate(s.id)}
                    >
                      Hacer principal
                    </button>
                  )}
                </div>
              )}
            </Tarjeta>
          ))}
        </div>
      )}

      {abierto && (
        <>
          <div style={{ height: 16 }} />
          <Tarjeta titulo={creando ? 'Nueva oficina' : 'Editar oficina'}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
              <Campo
                etiqueta="Código"
                requerido
                ayuda="Corto y en mayúsculas: PRI, BOG."
                valor={forma.codigo}
                alCambiar={(v) => setForma({ ...forma, codigo: v })}
                error={campo('codigo')}
              />
              <Campo
                etiqueta="Nombre"
                requerido
                valor={forma.nombre}
                alCambiar={(v) => setForma({ ...forma, nombre: v })}
                error={campo('nombre')}
              />
            </div>

            <div style={{ height: 12 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Seleccion
                etiqueta="Ciudad"
                requerido
                valor={forma.ciudadId}
                alCambiar={(v) => setForma({ ...forma, ciudadId: v })}
                vacio="Elige una"
                opciones={(ciudades.data ?? []).map((c) => ({
                  valor: c.id,
                  texto: `${c.nombre} · ${c.departamento}`,
                }))}
                error={campo('ciudadId')}
              />
              <Campo
                etiqueta="Slug"
                ayuda="Para la URL. Vacío, se calcula del nombre."
                valor={forma.slug}
                alCambiar={(v) => setForma({ ...forma, slug: v })}
                error={campo('slug')}
              />
            </div>

            <div style={{ height: 12 }} />
            <Campo
              etiqueta="Dirección"
              ayuda="Se publica en el portal."
              valor={forma.direccion}
              alCambiar={(v) => setForma({ ...forma, direccion: v })}
              error={campo('direccion')}
            />

            <div style={{ height: 12 }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <Campo
                etiqueta="Teléfono"
                valor={forma.telefono}
                alCambiar={(v) => setForma({ ...forma, telefono: v })}
                error={campo('telefono')}
              />
              <Campo
                etiqueta="WhatsApp"
                valor={forma.whatsapp}
                alCambiar={(v) => setForma({ ...forma, whatsapp: v })}
                error={campo('whatsapp')}
              />
              <Campo
                etiqueta="Correo"
                tipo="email"
                valor={forma.email}
                alCambiar={(v) => setForma({ ...forma, email: v })}
                error={campo('email')}
              />
            </div>

            <div style={{ height: 12 }} />
            <b style={{ fontSize: 14 }}>Horario</b>
            <p style={{ opacity: 0.7, fontSize: 13.5, margin: '4px 0 8px' }}>
              Como lo diría alguien: «8:00 a 12:00 y 2:00 a 6:00». Lo que dejes vacío no
              se publica.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {DIAS.map((dia) => (
                <Campo
                  key={dia.clave}
                  etiqueta={dia.texto}
                  valor={forma.horario[dia.clave] ?? ''}
                  alCambiar={(v) =>
                    setForma({ ...forma, horario: { ...forma.horario, [dia.clave]: v } })
                  }
                />
              ))}
            </div>

            <div style={{ height: 12 }} />
            <AreaTexto
              etiqueta="Descripción"
              valor={forma.descripcion}
              alCambiar={(v) => setForma({ ...forma, descripcion: v })}
            />

            <div style={{ height: 12 }} />
            <Casilla
              etiqueta="Activa"
              ayuda="Apagada, deja de aparecer en el portal."
              valor={forma.activa}
              alCambiar={(v) => setForma({ ...forma, activa: v })}
            />

            <div style={{ height: 16 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                disabled={
                  guardar.isPending ||
                  forma.nombre.trim() === '' ||
                  forma.codigo.trim() === '' ||
                  forma.ciudadId === ''
                }
                onClick={() => guardar.mutate()}
              >
                {guardar.isPending ? 'Guardando…' : 'Guardar'}
              </button>
              <button
                className="plano"
                onClick={() => {
                  setCreando(false);
                  setEditando(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </Tarjeta>
        </>
      )}
    </>
  );
}
